import styled from 'styled-components';
import {CustomSearchBar} from '@src/common/filters/CustomSearchBar';
import {useTranslation} from 'next-i18next';
import {useMemo, useState} from 'react';
import {devices} from '@src/common/screenSizes';
import {useQuery} from 'react-query';
import {useDebounce} from '@src/utils/useDebounce';
import {GSearch, Place} from '@src/common/inputs/GSearch';
import {CategoryApi} from '@src/api/category-api';
import {categoryService} from '@src/features/auth/logic/category-service';
import {ClubList} from '../components/ClubList';
import {SubCategory, SubSubCategory} from '@src/models/category';
import {SpinnerWrapped} from '@src/common/ui/SpinnerWrapped';
import {userKeys} from '@src/queries/user.queries';
import {UserApi} from '@src/api/user-api';
import {ClubApi} from '@src/api/club-api';
import {EventsApi} from '@src/api/events-api';
import {CommunityApi} from '@src/api/community-api';
import {FilterIconButton} from '@src/common/inputs/FilterIconButton';

const PickCLubWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
  max-width: 620px;
  margin: 16px auto;
  padding: 0 16px;

  @media ${devices.sm} {
    padding: 0 12px;
  }
`;

const Greeting = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const GreetingTitle = styled.h1`
  margin: 0;
  font-weight: 600;
  font-size: 26px;
  line-height: 1.25;
  color: ${(p) => p.theme.colors.primary500};
`;

const GreetingSubtitle = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.4;
  color: ${(p) => p.theme.colors.textMuted};
`;

const ChipsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ChipsRail = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Chip = styled.button<{$active?: boolean}>`
  flex-shrink: 0;
  padding: 9px 14px;
  border-radius: 999px;
  border: 1px solid
    ${(p) =>
      p.$active ? p.theme.colors.primary500 : p.theme.colors.violet300};
  background: ${(p) =>
    p.$active ? p.theme.colors.primary500 : p.theme.colors.secondary100};
  color: ${(p) =>
    p.$active ? p.theme.colors.neutral100 : p.theme.colors.primary500};
  font-size: 13px;
  font-weight: 500;
  line-height: 1.2;
  cursor: pointer;
  transition: background ${(p) => p.theme.motion.duration.fast}
      ${(p) => p.theme.motion.easing.out},
    border-color ${(p) => p.theme.motion.duration.fast}
      ${(p) => p.theme.motion.easing.out},
    color ${(p) => p.theme.motion.duration.fast}
      ${(p) => p.theme.motion.easing.out};

  &:hover {
    border-color: ${(p) => p.theme.colors.primary500};
  }
`;

const ShowMoreCategories = styled.button`
  align-self: flex-start;
  margin: 0;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.3;
  color: ${(p) => p.theme.colors.violet600};
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: ${(p) => p.theme.colors.primary500};
  }
`;

const AdvancedFilters = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StatsBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 8px;
  border-radius: 16px;
  background: ${(p) => p.theme.colors.secondary100};
`;

const StatItem = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`;

const StatValue = styled.span`
  font-weight: 600;
  font-size: 20px;
  line-height: 1.2;
  color: ${(p) => p.theme.colors.violet600};
`;

const StatLabel = styled.span`
  font-size: 11px;
  line-height: 1.3;
  text-align: center;
  color: ${(p) => p.theme.colors.textMuted};
`;

const StatDivider = styled.div`
  width: 1px;
  height: 32px;
  background: ${(p) => p.theme.colors.violet300};
  flex-shrink: 0;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 4px;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-weight: 600;
  font-size: 17px;
  line-height: 1.3;
  color: ${(p) => p.theme.colors.primary500};
`;

interface ClubListScreenProps {
  excludeJoined?: boolean;
}

const CATEGORY_PAGE_SIZE = 8;

export const ClubListScreen = ({excludeJoined}: ClubListScreenProps) => {
  const {t} = useTranslation();
  const [categoryId, setCategoryId] = useState('ALL');
  const [subCategoryData, setSubCategoryData] = useState<
    SubCategory[] | undefined
  >(undefined);
  const [subSubCategoryData, setSubSubCategoryData] = useState<
    SubSubCategory[] | undefined
  >(undefined);
  const [subCategoryId, setSubCategoryId] = useState('ALL');
  const [subSubCategoryId, setSubSubCategoryId] = useState('ALL');
  const [search, setSearch] = useState('');
  const [address, setAddress] = useState<Place>();
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [visibleCategoryCount, setVisibleCategoryCount] =
    useState(CATEGORY_PAGE_SIZE);

  const showPersonalChrome = Boolean(excludeJoined);

  const {data: user} = useQuery({
    queryKey: userKeys.myUser(),
    queryFn: () => UserApi.getCurrentUser(),
    enabled: showPersonalChrome,
    staleTime: 60 * 1000,
  });
  const {data, isLoading} = useQuery(
    ['categories'],
    () => CategoryApi.getCategories({recursively: true}),
    {
      staleTime: 1 * 60 * 1000,
    }
  );

  const {data: myClubsPage} = useQuery(
    ['home-stats-clubs'],
    () => ClubApi.getClubsWhereUserIsMember({page: 1, limit: 1}),
    {
      enabled: showPersonalChrome,
      staleTime: 60 * 1000,
    }
  );
  const {data: myTopicsPage} = useQuery(
    ['home-stats-topics'],
    () =>
      CommunityApi.getMyDiscussions({
        search: '',
        interestsIds: [],
        page: 1,
        languages: [],
      }),
    {
      enabled: showPersonalChrome,
      staleTime: 60 * 1000,
    }
  );
  const {data: myEventsPage} = useQuery(
    ['home-stats-events'],
    () => EventsApi.getMyEvents({search: '', companyIds: [], page: 1, limit: 1}),
    {
      enabled: showPersonalChrome,
      staleTime: 60 * 1000,
    }
  );

  const categoriesData = useMemo(() => {
    if (!data) {
      return [];
    }
    return categoryService.mapCategories(data);
  }, [data]);

  const chips = useMemo(
    () => [
      {id: 'ALL', label: t('common:enums.ALL')},
      ...categoriesData.map((c) => ({id: c.value, label: c.label})),
    ],
    [categoriesData, t]
  );

  const visibleChips = useMemo(() => {
    const activeIndex = chips.findIndex((chip) => chip.id === categoryId);
    const minVisible =
      activeIndex >= 0
        ? Math.max(visibleCategoryCount, activeIndex + 1)
        : visibleCategoryCount;
    return chips.slice(0, Math.min(minVisible, chips.length));
  }, [chips, categoryId, visibleCategoryCount]);

  const hasMoreCategories = visibleChips.length < chips.length;

  const subChips = useMemo(
    () => [
      {
        id: 'ALL',
        label: t('user-profile:club-list:all-categories'),
      },
      ...(subCategoryData ?? []).map((c) => ({id: c.value, label: c.label})),
    ],
    [subCategoryData, t]
  );

  const subSubChips = useMemo(
    () => [
      {
        id: 'ALL',
        label: t('user-profile:club-list:all-categories'),
      },
      ...(subSubCategoryData ?? []).map((c) => ({
        id: c.value,
        label: c.label,
      })),
    ],
    [subSubCategoryData, t]
  );

  const handleNewSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const handleNewSelectInputCategory = (e: string) => {
    setCategoryId(e);
    setSubCategoryId('ALL');
    setSubSubCategoryId('ALL');
    if (categoriesData)
      setSubCategoryData(
        categoryService.getSubcategoriesForCategory(categoriesData, e)
      );
  };
  const handleNewSelectInputSubCategory = (e: string) => {
    setSubCategoryId(e);
    setSubSubCategoryId('ALL');
    if (subCategoryData)
      setSubSubCategoryData(
        categoryService.getSubSubcategoriesForSubcategory(categoriesData, e)
      );
  };
  const handleNewSelectInputSubSubCategory = (e: string) => {
    setSubSubCategoryId(e);
  };

  const debouncedSearch = useDebounce(search, 500);
  const debouncedAddress = useDebounce(address, 500);

  const handleNewAddress = (e: Place) => {
    setAddress(e);
  };
  const handleResetAddress = () => {
    setAddress({
      latitude: '',
      longitude: '',
      address: '',
      country: '',
      countryShortName: '',
    });
  };

  const hasActiveLocation = Boolean(
    address?.address || address?.city || address?.latitude
  );
  const showAdvanced =
    categoryId !== 'ALL' || showAdvancedFilters || hasActiveLocation;

  if (isLoading) {
    return (
      <Box>
        <SpinnerWrapped />
      </Box>
    );
  }

  return (
    <PickCLubWrapper>
      <CustomSearchBar
        onChange={handleNewSearchInput}
        value={search}
        placeHolder={
          t('common:search-club-by-name-president-categories') as string
        }
      />

      {showPersonalChrome && user?.name ? (
        <Greeting>
          <GreetingTitle>
            {t('common:hello')}, {user.name}
          </GreetingTitle>
          <GreetingSubtitle>{t('common:discover-clubs')}</GreetingSubtitle>
        </Greeting>
      ) : null}

      <ChipsSection>
        <ChipsRail>
          {visibleChips.map((chip) => {
            const active = categoryId === chip.id;
            return (
              <Chip
                key={chip.id}
                type="button"
                $active={active}
                onClick={() => handleNewSelectInputCategory(chip.id)}
              >
                {chip.label}
              </Chip>
            );
          })}
        </ChipsRail>
        {hasMoreCategories ? (
          <ShowMoreCategories
            type="button"
            onClick={() =>
              setVisibleCategoryCount((count) => {
                const activeIndex = chips.findIndex(
                  (chip) => chip.id === categoryId
                );
                const currentlyShown = Math.max(
                  count,
                  activeIndex >= 0 ? activeIndex + 1 : 0
                );
                return Math.min(
                  currentlyShown + CATEGORY_PAGE_SIZE,
                  chips.length
                );
              })
            }
          >
            {t('common:show-more')}
          </ShowMoreCategories>
        ) : null}
      </ChipsSection>

      {showAdvanced ? (
        <AdvancedFilters>
          {categoryId !== 'ALL' ? (
            <ChipsRail>
              {subChips.map((chip) => {
                const active = subCategoryId === chip.id;
                return (
                  <Chip
                    key={chip.id}
                    type="button"
                    $active={active}
                    onClick={() => handleNewSelectInputSubCategory(chip.id)}
                  >
                    {chip.label}
                  </Chip>
                );
              })}
            </ChipsRail>
          ) : null}
          {subCategoryId !== 'ALL' ? (
            <ChipsRail>
              {subSubChips.map((chip) => {
                const active = subSubCategoryId === chip.id;
                return (
                  <Chip
                    key={chip.id}
                    type="button"
                    $active={active}
                    onClick={() => handleNewSelectInputSubSubCategory(chip.id)}
                  >
                    {chip.label}
                  </Chip>
                );
              })}
            </ChipsRail>
          ) : null}
          <GSearch
            type="city"
            inputType="secondary"
            value={address}
            label={''}
            handleNewPlace={handleNewAddress}
            onResetFilter={handleResetAddress}
            placeHolder={t('common:localisation') as string}
          />
        </AdvancedFilters>
      ) : null}

      {showPersonalChrome ? (
        <StatsBanner>
          <StatItem>
            <StatValue>{myClubsPage?.meta?.total ?? 0}</StatValue>
            <StatLabel>{t('common:memberships')}</StatLabel>
          </StatItem>
          <StatDivider />
          <StatItem>
            <StatValue>{myTopicsPage?.meta?.total ?? 0}</StatValue>
            <StatLabel>{t('common:new-posts')}</StatLabel>
          </StatItem>
          <StatDivider />
          <StatItem>
            <StatValue>{myEventsPage?.meta?.total ?? 0}</StatValue>
            <StatLabel>{t('my-profile:my_dates')}</StatLabel>
          </StatItem>
        </StatsBanner>
      ) : null}

      <SectionHeader>
        <SectionTitle>{t(`user-profile:club-list:CLUBS`)}</SectionTitle>
        <FilterIconButton
          active={showAdvancedFilters || hasActiveLocation}
          aria-label={t('common:filter') as string}
          title={t('common:filter') as string}
          onClick={() => setShowAdvancedFilters((v) => !v)}
        />
      </SectionHeader>

      <ClubList
        categoryId={categoryId}
        subCategoryId={subCategoryId}
        subSubCategoryId={subSubCategoryId}
        search={debouncedSearch}
        address={debouncedAddress as Place}
        sort={'CREATED_AT'}
        excludeJoined={excludeJoined}
      />
    </PickCLubWrapper>
  );
};

const Box = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 620px;
  margin-left: auto;
  margin-right: auto;
`;
